import { FC, useMemo, useState, useEffect } from 'react'
import { Box, Button, Checkbox, ListItemText, MenuItem, OutlinedInput, SelectProps, Typography } from '@mui/material'
import { InstanceCheckedIcon, InstanceUncheckedIcon, SelectDropdownIcon } from 'ui/components/svg'
import { CategoryMenuProps, JiraFormControl, JiraSelect, Wrapper } from './auditLogSelect.styles'
import JiraDropdownSearch from '../../integrations/jira-integration/jira-dropdowns/JiraDropdownSearch'
import { User } from 'models'
import theme from 'theme'
import { escapeRegExp } from 'lib/utils'

export interface LabelOption {
  name: string
  label: string
  icon?: JSX.Element
  action?: () => void
  id?: string
  integration_name?: string
  disabled?: boolean
  email?: string
  enabledForField?: string
}

interface Props extends SelectProps {
  options: User[]
  value: User[]
  handleChange: any
  minWidth?: string
  iscustom?: string
  hideSearch?: boolean
  label?: string
  maxWidth?: string
  wrapperSx?: Record<string, unknown>
  handleReset?: any
}

const AuditLogSelect: FC<Props> = ({
  label,
  options,
  handleChange,
  iscustom,
  placeholder,
  minWidth,
  value,
  hideSearch = false,
  wrapperSx,
  maxWidth,
  handleReset,
  ...rest
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [search, setSearch] = useState('')
  const [labelOptions, setLabelOptions] = useState<User[]>(options)

  useEffect(() => {
    setLabelOptions(options)
  }, [options])

  useEffect(() => {
    setTimeout(() => {
      if (!search) setLabelOptions([...options])

      const filteredItems = options.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()))
      setLabelOptions([...filteredItems])
    }, 300)
  }, [search])

  useEffect(() => {
    if (!anchorEl) {
      setSearch('')
    }
  }, [anchorEl])

  const highlightMatch = (text: string) => {
    if (!search) return text

    const regex = new RegExp(`(${escapeRegExp(search)})`, 'gi')
    return text.split(regex).map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ color: theme.colors.textPrimary }}>
          {part}
        </span>
      ) : (
        part
      )
    )
  }

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const renderValue = (): string => {
    const pruned = value.filter((s) => s.name !== '')
    if (!pruned.length) return placeholder || ''

    const option = options.find((n) => n.name === pruned[0].name)

    if (option) {
      if (pruned.length === 1) {
        return option.name
      }

      if (placeholder && pruned.length === 0) {
        return placeholder
      }

      return `${pruned.length} selected`
    }

    return placeholder || ''
  }

  const svgClassname = useMemo(() => {
    let base = 'MuiSelect-icon MuiSelect-iconOutlined MuiSelect-iconClosed svg-dropdown'
    if (anchorEl) {
      base = base.replaceAll('MuiSelect-iconClosed', 'MuiSelect-iconOpen svg-dropdown')
    } else {
      base = base.replaceAll('MuiSelect-iconOpen', 'MuiSelect-iconClosed svg-dropdown')
    }

    return base
  }, [anchorEl])

  return (
    <Wrapper className="jira-dropdown-multiple-wrapper" {...wrapperSx}>
      {label && <Typography className="label-text">{label}</Typography>}

      <JiraFormControl
        sx={{ minWidth, maxWidth }}
        isselected={anchorEl || (placeholder !== 'Sort' && value.length) ? 'true' : 'false'}
        iscustom={iscustom}
        isopened={anchorEl ? 'true' : 'false'}
      >
        <JiraSelect
          isselected={placeholder !== 'Sort' && value.length > 1 ? 'true' : 'false'}
          iscustom={iscustom}
          labelId="library-select-label"
          id="library-select"
          displayEmpty
          MenuProps={{
            ...CategoryMenuProps,
            PaperProps: {
              ...CategoryMenuProps.PaperProps,
              sx: {
                ...CategoryMenuProps.PaperProps.sx,
                '.MuiList-root': {
                  ...CategoryMenuProps.PaperProps.sx['.MuiList-root'],
                  padding: `${theme.spacing.xSmall}px !important`
                },
                display: anchorEl ? 'block' : 'none'
              }
            },
            anchorEl,
            disableAutoFocusItem: true,
            autoFocus: false,
            open: Boolean(anchorEl),
            onClose: () => setAnchorEl(null)
          }}
          IconComponent={() => <SelectDropdownIcon className={svgClassname} />}
          renderValue={() => renderValue()}
          inputProps={{ 'aria-label': 'Without label' }}
          {...rest}
          value={value}
          onChange={handleChange}
          input={<OutlinedInput onMouseDown={handleClick} />}
          sx={{ minWidth }}
        >
          {!hideSearch && <JiraDropdownSearch search={search} setSearch={setSearch} placeholder="Search..." />}

          <Typography className="results-header-text">Results ({labelOptions.length})</Typography>

          <Box className="list-wrapper">
            {!labelOptions.length ? (
              <Box className="no-options-wrapper">
                <Typography className="no-options-text">No results found</Typography>
              </Box>
            ) : (
              labelOptions.map((option, index: number) => {
                const emails = value.map((c) => c.email)

                return (
                  <MenuItem
                    disableRipple
                    autoFocus={false}
                    key={index}
                    value={option.name}
                    onClick={() => {
                      const newValue: User[] = [...value]
                      if (option.email) {
                        if (!emails.includes(option.email)) {
                          newValue.push(option)
                        } else {
                          const index = emails.indexOf(option.email)
                          newValue.splice(index, 1)
                        }
                        handleChange({ target: { value: newValue } })
                        if (!rest.multiple) {
                          setAnchorEl(null)
                        }
                        setSearch('')
                      }
                    }}
                  >
                    {rest.multiple && (
                      <Checkbox
                        checked={emails.indexOf(option.email) > -1}
                        icon={<InstanceUncheckedIcon />}
                        checkedIcon={<InstanceCheckedIcon />}
                      />
                    )}
                    <ListItemText primary={highlightMatch(option.name)} />
                  </MenuItem>
                )
              })
            )}
            {typeof handleReset !== 'undefined' && (
              <MenuItem
                className="reset-button-wrapper"
                disableRipple
                disabled={value.length === 1 && value[0].name === ''}
              >
                <Button
                  className="reset-button"
                  onClick={() => {
                    handleReset()
                    setSearch('')
                  }}
                >
                  Reset
                </Button>
              </MenuItem>
            )}
          </Box>
        </JiraSelect>
      </JiraFormControl>
    </Wrapper>
  )
}

export default AuditLogSelect
